import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { AlertTriangle, Copy, FolderKanban, RefreshCw, Search } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { EmptyState, ErrorState, LoadingState } from '@/components/composites/states'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { qk } from '@/api/query-keys'
import { cn } from '@/lib/utils'
import { pipelineApi, usePipelineBoard, type BoardDeal, type Stage } from './api'
import { DealSheet } from './DealSheet'
import { daysAgo, Dot, LEVEL_SOFT, money, NotInGhl, PipelineFrame, shortDay, STAGE_GROUP, STATUS_LABEL, STATUS_STYLE } from './shared'

const PILOT_STAGES: Stage[] = ['pilot_agreed', 'pilot_running', 'pilot_review', 'contract']

/**
 * C1 / P6 — every deal from demo to contract on one board. `?view=pilots` is the Pipeline and
 * pilots board Project Management opens: the pilot stages only, with each pilot's project.
 */
export function BoardPage() {
  const [params, setParams] = useSearchParams()
  const board = usePipelineBoard()
  const me = useIdentity()
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [now] = useState(() => Date.now())
  const view = params.get('view') === 'pilots' ? 'pilots' : 'all'
  const mine = params.get('owner') === 'me'
  const flag = params.get('flag') ?? ''
  const openId = params.get('deal')

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const deals = useMemo(() => {
    const all = board.data?.deals ?? []
    const needle = q.trim().toLowerCase()
    return all.filter(
      (d) =>
        (view === 'all' || PILOT_STAGES.includes(d.stage)) &&
        (!mine || d.owner.memberId === me.userId) &&
        (!needle || `${d.name} ${d.company.name} ${d.owner.name ?? ''}`.toLowerCase().includes(needle)) &&
        (!flag ||
          (flag === 'noNextStep' && !d.nextStep) ||
          (flag === 'stale' && d.stale) ||
          (flag === 'red' && d.level === 'red') ||
          (flag === 'noValue' && !(d.value && d.value > 0) && ['pilot_agreed', 'pilot_running', 'pilot_review', 'contract'].includes(d.stage)) ||
          (flag === 'missingFacts' && (d.stage === 'pilot_running' || d.stage === 'pilot_review') && d.factsDone < 5) ||
          (flag === 'duplicates' && d.duplicateOf.length > 0)),
    )
  }, [board.data, q, view, mine, me.userId, flag])

  const refresh = async () => {
    setRefreshing(true)
    try {
      const fresh = await pipelineApi.board(true)
      qc.setQueryData(qk.pipelineBoard(), fresh)
    } finally {
      setRefreshing(false)
    }
  }

  const stages = (board.data?.stages ?? []).filter((s) => view === 'all' || PILOT_STAGES.includes(s.key))
  const h = board.data?.hygiene
  const dupCount = (board.data?.deals ?? []).filter((d) => d.duplicateOf.length).length

  return (
    <PipelineFrame
      title={view === 'pilots' ? 'Pipeline and pilots' : 'Pipeline'}
      description={view === 'pilots' ? 'Every pilot from agreed to contract, with its project and how both are doing. Late decisions come up red.' : 'Every deal from demo to signed contract on one board. Each card says what happens next and by when.'}
      actions={
        <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={refreshing}>
          <RefreshCw className={cn('size-4', refreshing && 'animate-spin')} />Refresh from GHL
        </Button>
      }
    >
      {board.error ? (
        <ErrorState error={board.error} onRetry={() => void board.refetch()} />
      ) : board.isPending ? (
        <LoadingState label="Loading the pipeline from GoHighLevel" />
      ) : !board.data.connected ? (
        <EmptyState title="GoHighLevel is not connected" description="Connect it under Settings → Integrations and the pipeline fills itself." />
      ) : (
        <>
          {/* T3 — the hygiene line, each count a filter */}
          {h ? (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm shadow-sm">
              <span className="font-medium">{h.cleanPct}% up to date</span>
              <span className="text-muted-foreground">· {h.line}</span>
              <span className="ml-auto flex flex-wrap gap-1.5">
                {(
                  [
                    ['noNextStep', `${h.counts.noNextStep} no next step`, h.counts.noNextStep],
                    ['red', 'Needs action', (board.data.deals ?? []).filter((d) => d.level === 'red').length],
                    ['stale', `${h.counts.stale} stale`, h.counts.stale],
                    ['noValue', `${h.counts.noValue} no value`, h.counts.noValue],
                    ['missingFacts', `${h.counts.missingFacts} missing facts`, h.counts.missingFacts],
                    ['duplicates', `${dupCount} duplicates`, dupCount],
                  ] as const
                )
                  .filter(([, , n]) => n > 0)
                  .map(([key, label]) => (
                    <button key={key} type="button" aria-pressed={flag === key} onClick={() => set('flag', flag === key ? null : key)} className={cn('rounded-full border px-2.5 py-0.5 text-xs', flag === key ? 'border-brand bg-accent font-medium' : 'border-border text-muted-foreground hover:bg-accent')}>
                      {label}
                    </button>
                  ))}
              </span>
            </div>
          ) : null}

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm" role="group" aria-label="Which deals">
              {(['all', 'pilots'] as const).map((v) => (
                <button key={v} type="button" aria-pressed={view === v} onClick={() => set('view', v === 'all' ? null : v)} className={cn('rounded-md px-3 py-1', view === v ? 'bg-accent font-medium' : 'text-muted-foreground')}>
                  {v === 'all' ? 'Whole pipeline' : 'Pilots only'}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm" role="group" aria-label="Whose deals">
              {([false, true] as const).map((v) => (
                <button key={String(v)} type="button" aria-pressed={mine === v} onClick={() => set('owner', v ? 'me' : null)} className={cn('rounded-md px-3 py-1', mine === v ? 'bg-accent font-medium' : 'text-muted-foreground')}>
                  {v ? 'Mine' : 'Everyone'}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-text-muted" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals" className="h-8 w-56 pl-8" />
            </div>
            {!board.data.settings.ghlWrites ? (
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <NotInGhl /> Moves and values made here stay in Tapwork: writing to GoHighLevel is off.
              </span>
            ) : null}
          </div>

          <div className="flex gap-3 overflow-x-auto pb-4">
            {stages.map((s) => {
              const here = deals.filter((d) => d.stage === s.key)
              const total = here.reduce((n, d) => n + (d.value ?? 0), 0)
              return (
                <section key={s.key} className="flex w-72 shrink-0 flex-col rounded-xl bg-surface-sunken p-2" aria-label={s.label}>
                  <header className="mb-2 px-1">
                    <div className="text-tiny font-semibold uppercase tracking-wide text-text-muted">{STAGE_GROUP[s.key]}</div>
                    <div className="flex items-baseline justify-between gap-2">
                      <h2 className="text-sm font-semibold">{s.label} <span className="num font-normal text-muted-foreground">{here.length}</span></h2>
                      {board.data.can.seeValue && total > 0 ? <span className="num text-xs text-muted-foreground">{money(total)}</span> : null}
                    </div>
                  </header>
                  <ul className="grid grid-cols-[minmax(0,1fr)] gap-2">
                    {here.map((d) => (
                      <li key={d.id} className="min-w-0">
                        <DealCard deal={d} now={now} onOpen={() => set('deal', d.id)} />
                      </li>
                    ))}
                    {here.length === 0 ? <li className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">Nothing here</li> : null}
                  </ul>
                </section>
              )
            })}
          </div>
        </>
      )}
      <DealSheet id={openId} onClose={() => set('deal', null)} onOpen={(id) => set('deal', id)} />
    </PipelineFrame>
  )
}

function DealCard({ deal: d, now, onOpen }: { deal: BoardDeal; now: number; onOpen: () => void }) {
  const pilot = d.stage === 'pilot_running' || d.stage === 'pilot_review'
  return (
    <button type="button" onClick={onOpen} className="w-full rounded-lg border border-border bg-card p-3 text-left shadow-sm transition hover:border-border-strong hover:shadow-md">
      <div className="flex items-start gap-2">
        <Dot level={d.level} className="mt-1.5" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{d.company.name}</div>
          {d.name !== d.company.name ? <div className="truncate text-xs text-muted-foreground">{d.name}</div> : null}
        </div>
        {d.value !== null ? <span className={cn('num shrink-0 text-xs font-medium', d.valuePending && 'text-warning')}>{d.value ? money(d.value) : 'No value'}</span> : null}
      </div>

      <div className={cn('mt-2 rounded-md px-2 py-1.5 text-xs', d.nextStep ? 'bg-surface-sunken' : 'bg-warning-soft text-warning')}>
        {d.nextStep ? (
          <>
            <span className="font-medium">Next:</span> {d.nextStep}
            {d.nextStepAt ? <span className={cn('ml-1', Date.parse(d.nextStepAt) < now - 43_200_000 ? 'font-medium text-danger' : 'text-muted-foreground')}>· {shortDay(d.nextStepAt)}</span> : null}
          </>
        ) : (
          <span className="inline-flex items-center gap-1"><AlertTriangle className="size-3" />No next step</span>
        )}
      </div>

      {pilot ? (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div className={cn('h-full rounded-full', d.factsDone === 5 ? 'bg-success' : 'bg-warning')} style={{ width: `${(d.factsDone / 5) * 100}%` }} />
          </div>
          {d.factsDone}/5 facts
          {d.facts.decisionAt ? <span className={cn(d.decisionOverdue && 'font-medium text-danger')}>· decide {shortDay(d.facts.decisionAt)}</span> : null}
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-tiny text-muted-foreground">
        <span>{d.owner.name ?? 'No owner'}</span>
        <span>· {d.daysInStage}d in stage</span>
        <span>· contact {daysAgo(d.lastContactAt)}</span>
      </div>

      {(d.project || !d.inGhl || d.duplicateOf.length || d.dealStatus) ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {d.dealStatus ? <span className={cn('rounded-full px-1.5 py-0.5 text-tiny font-medium', STATUS_STYLE[d.dealStatus])}>{STATUS_LABEL[d.dealStatus]}</span> : null}
          {d.project ? (
            <span className={cn('inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-tiny', d.project.health ? LEVEL_SOFT[d.project.health] : 'bg-muted text-text-secondary')} title={`Client Project: ${d.project.name}, ${d.project.phaseLabel}`}>
              <FolderKanban className="size-3" />{d.project.phaseLabel}
            </span>
          ) : null}
          {!d.inGhl ? <NotInGhl /> : null}
          {d.duplicateOf.length ? <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-1.5 py-0.5 text-tiny text-warning"><Copy className="size-3" />Duplicate</span> : null}
        </div>
      ) : null}
    </button>
  )
}
